import { Store } from '../state/store';
import type { Conversation } from '../state/types';
import { UserMenuComponent } from './auth';
import { Component } from './base';

export class SidebarComponent extends Component {
    private store: Store;
    private userMenu: UserMenuComponent | null = null;
    private currentDropdownTargetId: string | null = null;
    private dropdownElement: HTMLElement | null = null;
    private outsideClickHandler: ((e: MouseEvent) => void) | null = null;

    constructor(element: HTMLElement, store: Store) {
        super(element);
        this.store = store;
        this.subscribeToStore();
        this.render();
        this.bindEvents();
    }

    private subscribeToStore(): void {
        this.unsubscribers.push(this.store.subscribeToConversations(() => this.renderWithTransition()));
        this.unsubscribers.push(this.store.subscribeToCurrentConversationId(() => this.renderWithTransition()));
        this.unsubscribers.push(this.store.subscribeToStreamingStates(() => this.updateStreamingIndicators()));
    }

    private renderWithTransition(): void {
        if ('startViewTransition' in document) {
            (document as any).startViewTransition(() => this.render());
        } else {
            this.render();
        }
    }

    destroy(): void {
        super.destroy();
        if (this.userMenu) {
            this.userMenu.destroy();
        }
        if (this.outsideClickHandler) {
            document.removeEventListener('click', this.outsideClickHandler);
        }
    }

    protected render(): void {
        const conversations = this.store.getConversations();
        
        // Close any open dropdown before re-render
        this.hideDropdown();
        
        this.element.innerHTML = `
            <div class="sidebar-header">
                <button id="sidebar-new-chat" class="new-chat-button" aria-label="Create new chat">
                    <i data-lucide="plus-square" class="new-chat-icon"></i>
                    <span>New Chat</span>
                </button>
            </div>
            <div class="conversations-list" id="conversations-list">
                ${conversations.map(conv => this.renderConversationItem(conv)).join('')}
                <!-- 
                    SINGLETON DROPDOWN PATTERN
                    One shared dropdown for all tabs, positioned dynamically based on clicked tab.
                    Placed at list level to escape individual tab stacking contexts caused by
                    view-transition-name (which creates isolated stacking contexts per tab).
                    This prevents z-index issues where dropdowns appear under other tabs.
                -->
                <div class="conversation-dropdown" id="conversation-dropdown">
                    <button class="conversation-dropdown-item delete-btn" id="dropdown-delete-btn">Delete</button>
                </div>
            </div>
            <div class="sidebar-footer">
                <div id="user-menu-container"></div>
            </div>
        `;
        
        // Store reference to dropdown
        this.dropdownElement = this.element.querySelector('#conversation-dropdown') as HTMLElement;
        
        (window as any).lucide?.createIcons();
        this.bindEvents();
        this.bindConversationEvents();
        this.bindDropdownEvents();

        const userMenuEl = document.getElementById('user-menu-container');
        if (userMenuEl) {
            this.userMenu = new UserMenuComponent(userMenuEl);
            this.userMenu.render();
        }
    }

    private renderConversationItem(conv: Conversation): string {
        const currentConversationId = this.store.getCurrentConversationId();
        const isActive = conv.id === currentConversationId;
        const streamingStates = this.store.getStreamingState(conv.id);
        const isStreaming = streamingStates.isStreaming;

        return `
            <div class="conversation-tab ${isActive ? 'active' : ''}" data-id="${conv.id}" style="--tab-name: tab-${conv.id}">
                <div class="conversation-title">${conv.title || 'New Chat'}</div>
                <div class="conversation-menu">
                    <button class="conversation-menu-btn ${isStreaming ? 'streaming' : ''}" data-id="${conv.id}">
                        <i data-lucide="more-vertical"></i>
                    </button>
                </div>
            </div>
        `;
    }

    private bindEvents(): void {
        this.element.querySelector('#sidebar-new-chat')?.addEventListener('click', () => {
            this.dispatch('new-chat');
        });
    }

    private bindConversationEvents(): void {
        const conversationsList = this.element.querySelector('#conversations-list');
        if (!conversationsList) return;

        conversationsList.querySelectorAll('.conversation-tab').forEach(tab => {
            const titleElement = tab.querySelector('.conversation-title') as HTMLElement;
            if (titleElement && titleElement.scrollWidth > titleElement.clientWidth) {
                titleElement.title = titleElement.textContent || '';
            }

            tab.addEventListener('click', (e) => {
                if (!(e.target as HTMLElement).classList.contains('conversation-menu-btn')) {
                    const id = tab.getAttribute('data-id');
                    if (id) this.dispatch('select-conversation', id);
                }
            });
        });

        conversationsList.querySelectorAll('.conversation-menu-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const id = btn.getAttribute('data-id');
                if (id) this.toggleDropdown(id, btn as HTMLElement);
            });
        });
    }

    private bindDropdownEvents(): void {
        const deleteBtn = this.element.querySelector('#dropdown-delete-btn');
        if (!deleteBtn) return;

        deleteBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            if (this.currentDropdownTargetId && confirm('Are you sure you want to delete this conversation?')) {
                this.dispatch('delete-conversation', this.currentDropdownTargetId);
            }
            this.hideDropdown();
        });

        // Set up outside click handler
        this.outsideClickHandler = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (this.dropdownElement?.classList.contains('show') &&
                !this.dropdownElement.contains(target) &&
                !target.closest('.conversation-menu-btn')) {
                this.hideDropdown();
            }
        };
        document.addEventListener('click', this.outsideClickHandler);
    }

    private toggleDropdown(id: string, triggerBtn: HTMLElement): void {
        if (!this.dropdownElement) return;

        // If clicking same tab that's already open, close it
        if (this.currentDropdownTargetId === id && this.dropdownElement.classList.contains('show')) {
            this.hideDropdown();
            return;
        }

        // Store the target ID for delete action reference
        this.currentDropdownTargetId = id;

        // CRITICAL: Position relative to the conversation tab, NOT the trigger button.
        // The dropdown must be full-width aligned with the tab to avoid z-index issues
        // caused by view-transition-name creating isolated stacking contexts per tab.
        const tab = triggerBtn.closest('.conversation-tab') as HTMLElement;
        if (!tab) return;

        const list = this.element.querySelector('#conversations-list') as HTMLElement;
        if (!list) return;

        // Use offset properties for container-relative positioning within the scrollable list.
        // getBoundingClientRect() cannot be used because it returns viewport coordinates,
        // which don't account for scroll position within the overflow container.
        const tabOffsetTop = tab.offsetTop;
        const tabOffsetLeft = tab.offsetLeft;
        const tabHeight = tab.offsetHeight;
        const tabWidth = tab.offsetWidth;
        
        // Measure dropdown height by briefly showing it (hidden from view).
        // We need the actual rendered height to calculate positioning correctly.
        this.dropdownElement.style.visibility = 'hidden';
        this.dropdownElement.style.display = 'block';
        const dropdownHeight = this.dropdownElement.offsetHeight;
        this.dropdownElement.style.display = '';
        this.dropdownElement.style.visibility = '';
        
        const margin = 8;
        const listHeight = list.clientHeight;
        const listScrollTop = list.scrollTop;
        
        // Calculate available space accounting for current scroll position.
        // This determines whether dropdown should appear above or below the tab.
        const tabBottomInView = tabOffsetTop + tabHeight - listScrollTop;
        const spaceBelow = listHeight - tabBottomInView;
        const spaceAbove = tabOffsetTop - listScrollTop;
        
        // Flip to above if: not enough space below AND enough space above
        const positionAbove = spaceBelow < dropdownHeight + margin && spaceAbove > dropdownHeight;
        
        if (positionAbove) {
            // Position dropdown above the tab (accounts for scroll position automatically via offsetTop)
            const top = tabOffsetTop - dropdownHeight - margin;
            this.dropdownElement.style.top = `${top}px`;
        } else {
            // Default: position dropdown below the tab
            const top = tabOffsetTop + tabHeight + margin;
            this.dropdownElement.style.top = `${top}px`;
        }
        
        // Right-align with the tab edge. The +12 accounts for the list's padding (12px on each side),
        // ensuring the dropdown aligns with the tab's right edge, not the container's edge.
        const right = list.clientWidth - (tabOffsetLeft + tabWidth) + 12;
        this.dropdownElement.style.right = `${right}px`;

        // Show the dropdown
        this.dropdownElement.classList.add('show');
    }

    private hideDropdown(): void {
        if (this.dropdownElement) {
            this.dropdownElement.classList.remove('show');
        }
        this.currentDropdownTargetId = null;
    }

    private updateStreamingIndicators(): void {
        const conversations = this.store.getConversations();
        conversations.forEach(conv => {
            const tab = this.element.querySelector(`.conversation-tab[data-id="${conv.id}"]`);
            if (!tab) return;
            
            const menuBtn = tab.querySelector('.conversation-menu-btn');
            if (!menuBtn) return;
            
            const streamingState = this.store.getStreamingState(conv.id);
            if (streamingState.isStreaming) {
                menuBtn.classList.add('streaming');
            } else {
                menuBtn.classList.remove('streaming');
            }
        });
    }

    private dispatch(type: string, detail?: any): void {
        this.element.dispatchEvent(new CustomEvent(type, { detail, bubbles: true }));
    }
}
